import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { Icon, Spinner, TypoGraph } from 'components'
import {
  useFeatureFlag,
  useReportConfig,
  useRefreshReportingContext,
  useIsReportingReady,
} from 'contexts/ReportingContext'
import { getReportFlagKey, ReportKey } from 'constants/reporting'
import { isCarrierLogin } from 'tools/auth'
import strings from 'strings'

const EMBED_READY_MESSAGE = 'tc-embed:ready'
const EMBED_AUTH_MESSAGE = 'tc-embed:auth'

interface ConsoleEmbedSlots {
  top?: React.ReactNode
  bottom?: React.ReactNode
}

interface ConsoleEmbedProps {
  reportKey: ReportKey
  ceaIds?: string
  start?: string
  end?: string
  invitingCompanyId?: string
  carrierId?: string
  eldProvider?: string
  locale?: string
  distanceUnit?: 'km' | 'mi'
  fullHeight?: boolean
  slots?: ConsoleEmbedSlots
  testId?: string
}

const ConsoleEmbed: React.FC<ConsoleEmbedProps> = ({
  reportKey,
  ceaIds,
  start,
  end,
  invitingCompanyId,
  carrierId,
  eldProvider,
  locale,
  distanceUnit,
  fullHeight,
  slots,
  testId = 'console-embed-iframe',
}) => {
  const showConsole = useFeatureFlag(getReportFlagKey(reportKey))
  const config = useReportConfig(reportKey)
  const refresh = useRefreshReportingContext()
  const [iframeError, setIframeError] = useState(false)
  const embedHeight = fullHeight ? 'calc(100vh - 95px)' : '77vh'
  const baseUrl = config?.baseUrl
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (!baseUrl) return
    let expectedOrigin: string
    try {
      expectedOrigin = new URL(baseUrl).origin
    } catch {
      return
    }
    let handshakeDone = false
    const sendToken = () => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: EMBED_AUTH_MESSAGE, token: localStorage.getItem('authToken') ?? '' },
        expectedOrigin
      )
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return
      if (event.source !== iframeRef.current?.contentWindow) return
      if (event.data?.type !== EMBED_READY_MESSAGE) return
      handshakeDone = true
      sendToken()
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'authToken' && handshakeDone) sendToken()
    }
    const onAuthStateChange = () => {
      if (handshakeDone) sendToken()
    }
    window.addEventListener('message', onMessage)
    window.addEventListener('storage', onStorage)
    window.addEventListener('authStateChange', onAuthStateChange)
    return () => {
      window.removeEventListener('message', onMessage)
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('authStateChange', onAuthStateChange)
    }
  }, [baseUrl])

  // Assumes at most one fullHeight ConsoleEmbed is mounted at a time (portfolio tabs are mutually exclusive).
  // If two ever mount simultaneously the second cleanup would incorrectly re-enable scroll for the first.
  useEffect(() => {
    if (!fullHeight) return
    document.body.style.overflowY = 'hidden'
    return () => {
      document.body.style.overflowY = ''
    }
  }, [fullHeight])

  const iframeUrl = useMemo(() => {
    if (!config) return null
    try {
      const url = new URL('/reporting/embed', config.baseUrl)
      url.searchParams.set('type', config.reportType)
      if (start) url.searchParams.set('start', start)
      if (end) url.searchParams.set('end', end)
      if (ceaIds) url.searchParams.set('cea_ids', ceaIds)
      if (invitingCompanyId)
        url.searchParams.set('invitingCompanyId', invitingCompanyId)
      if (carrierId) url.searchParams.set('carrierId', carrierId)
      if (eldProvider) url.searchParams.set('eldProvider', eldProvider)
      if (locale) url.searchParams.set('locale', locale)
      if (distanceUnit) url.searchParams.set('distanceUnit', distanceUnit)
      return url.toString()
    } catch {
      return null
    }
  }, [
    config,
    ceaIds,
    start,
    end,
    invitingCompanyId,
    carrierId,
    eldProvider,
    locale,
    distanceUnit,
  ])

  const isReady = useIsReportingReady()

  const showError =
    iframeError ||
    (isCarrierLogin() && !invitingCompanyId) ||
    (!!config && !iframeUrl) ||
    (isReady && !config)

  const iframeContent = () => {
    // Flags/config are fetched async and the cache resets on a full page reload —
    // until isReady, we don't actually know if the report is disabled or just not
    // loaded yet. Show the spinner rather than guessing "disabled" from a false
    // default (would otherwise flash REPORT_DISABLED on every reload).
    if (!isReady) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: embedHeight,
          }}
        >
          <Spinner />
        </Box>
      )
    }

    if (!showConsole) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            height: embedHeight,
          }}
        >
          <Icon materialIcon="ErrorOutline" size={48} color="#9e9e9e" />
          <TypoGraph
            variant="h4"
            align="center"
            color="#9e9e9e"
            mb={0}
            content={strings.CONSOLE_EMBED.REPORT_DISABLED}
          />
        </Box>
      )
    }

    if (showError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            height: embedHeight,
          }}
        >
          <Icon materialIcon="ErrorOutline" size={48} color="#9e9e9e" />
          <TypoGraph
            variant="h4"
            align="center"
            color="#9e9e9e"
            mb={0}
            content={strings.CONSOLE_EMBED.REPORT_UNAVAILABLE}
          />
        </Box>
      )
    }

    if (!iframeUrl) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: embedHeight,
          }}
        >
          <Spinner />
        </Box>
      )
    }

    return (
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        title={`${reportKey} report`}
        data-testid={testId}
        style={{ width: '100%', height: embedHeight, border: 'none' }}
        onError={() => setIframeError(true)}
      />
    )
  }

  // Same isReady guard as iframeContent — showConsole defaults false until the
  // reporting context loads, so gating slots on it alone would flash them hidden
  // on every page reload.
  const showSlots = isReady && showConsole

  return (
    <>
      {showSlots && slots?.top}
      {iframeContent()}
      {showSlots && slots?.bottom}
    </>
  )
}

export default ConsoleEmbed
